import authService from './authService';
import { VariableFee, VariableFeeUpsertPayload, VariableFeeFilter } from '../types/variableFee';
import { ApiResponse } from '../types/common';

/**
 * Variable Fees Service
 *
 * Gerencia custos variáveis mensais (impostos e comissão de vendedor) por empresa.
 * Endpoint base: /api/variable-fees
 */
class VariableFeeService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  /**
   * Busca os registros de custos variáveis de uma empresa em um ano.
   * GET /api/variable-fees?company_id={id}&year={year}
   */
  async getByCompanyAndYear(filter: VariableFeeFilter): Promise<VariableFee[]> {
    const params = new URLSearchParams({
      company_id: filter.company_id.toString(),
      year: filter.year.toString(),
    });
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/variable-fees?${params}`);
    if (!response.ok) throw new Error('Falha ao buscar custos variáveis');
    const json = await response.json();
    console.debug('[VariableFeeService] GET /variable-fees raw response:', JSON.stringify(json));
    const rows: VariableFee[] = (json.data ?? json) as VariableFee[];
    console.debug('[VariableFeeService] parsed rows:', rows);
    return rows;
  }

  /**
   * Cria ou atualiza um registro de custo variável (upsert).
   * PUT /api/variable-fees
   */
  async upsert(payload: VariableFeeUpsertPayload): Promise<VariableFee> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put('/variable-fees', payload);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.message || 'Falha ao salvar custo variável');
    }
    const json: ApiResponse<VariableFee> = await response.json();
    return json.data!;
  }

  /**
   * Remove um registro de custo variável.
   * DELETE /api/variable-fees?company_id={id}&reference_date={date}
   */
  async delete(companyId: number, referenceDate: string): Promise<void> {
    const params = new URLSearchParams({
      company_id: companyId.toString(),
      reference_date: referenceDate,
    });
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/variable-fees?${params}`);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body?.message || 'Falha ao excluir custo variável');
    }
  }
}

export const variableFeeService = new VariableFeeService();
export default variableFeeService;
